import {
  useClientEnableBrightSync,
  useClientUserSendInvitationToAccountEmail,
  useDeleteClient,
} from 'api/requests';
import {
  ClientEntity,
  OnboardingStatusType,
  PermissionType,
} from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { isDownloadQuestionnaireStatusSufficient } from 'modules/client/common/helpers';
import { useRevokeOtpVerification } from 'modules/client/common/hooks';

import { useDownloadClientQuestionnaire } from './useDownloadClientQuestionnaire';

interface Args {
  client: ClientEntity | null;
  refetch: () => Promise<any>;
  lockClientIfPossible: () => Promise<boolean>;
}

export const useClientActions = ({
  client,
  lockClientIfPossible,
  refetch,
}: Args) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const { checkPermissions } = usePermissions();

  const { revokeOtpVerification, revokeOtpVerificationLoading } =
    useRevokeOtpVerification();

  const { sendInvitationToAccountEmail } =
    useClientUserSendInvitationToAccountEmail();

  const { enableBrightSync, error: errorEnableBrightSync } =
    useClientEnableBrightSync();

  const { downloadClientQuestionnaire } = useDownloadClientQuestionnaire();
  const {
    deleteClient,
    loading: deleteLoading,
    error: deleteError,
  } = useDeleteClient();

  const onShowVersionHistory =
    client?.versionNumber && client?.versionNumber > 1
      ? () => {
          navigate(
            ROUTES.clients.personClients.personClient.personClientVersion
              .fullPath,
            {
              clientId: client.id!,
              versionId: client.versionId!,
            }
          );
        }
      : undefined;

  const onRevokeOtpVerification = client?.user.otpVerified
    ? async () => revokeOtpVerification(client?.user.id!, refetch)
    : undefined;

  const onSendInvitationToAccount =
    checkPermissions([PermissionType.Auth_SendInvitationToAccountEmail]) &&
    client?.onboardingStatus === OnboardingStatusType.AccountOpened &&
    !client?.user.isActive
      ? async () => {
          const res = await sendInvitationToAccountEmail(client!.user.id!);

          if (res.resendTime) {
            notify.info(t('common.invitationHasBeenSent'));
          }

          return res.resendTime;
        }
      : undefined;

  const onLinkToBright =
    client?.isBrightSyncEnabled === false &&
    checkPermissions([PermissionType.Client_LegalClient_EnableBrightSync])
      ? async () => {
          if (await lockClientIfPossible()) {
            const res = await enableBrightSync(
              client.id!,
              client.versionNumber!
            );
            if (res) {
              notify.info(t('client.client.statuses.updateBrightText'));
            }
          }
        }
      : undefined;

  const onDownloadClientQuestionnaire =
    client &&
    isDownloadQuestionnaireStatusSufficient(client.onboardingStatus) &&
    checkPermissions([PermissionType.Client_LegalClient_ExportQuestionnaire])
      ? () => downloadClientQuestionnaire(client.id)
      : undefined;

  const onDelete =
    checkPermissions([PermissionType.Client_LegalClient_DeleteIncompleteOne]) &&
    client?.onboardingStatus === OnboardingStatusType.FillingOut
      ? async () => {
          if (await deleteClient(client.id)) {
            navigate(
              ROUTES.clients.personClients.fullPath,
              {},
              { replace: true }
            );
          }
        }
      : undefined;

  useServerErrorNotify(errorEnableBrightSync || deleteError);

  return {
    revokeOtpVerificationLoading,
    onShowVersionHistory,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
    onLinkToBright,
    onDownloadClientQuestionnaire,
    onDelete,
    deleteLoading,
  };
};
